import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import React, { useEffect, useMemo, useState } from 'react';
import formatNumber from '../utils/formatNumber';
import CollectionButton from './CollectionButton';
import Pagination from './Pagination'; // Import the Pagination component
import SecondaryListing from './SecondaryListing';
import Spinner from './Spinner';

const collectionKeys = [
  'shitty-kitties',
  'shitty-kitties-slt',
  'shitty-kitties-third-litter',
  'shitty-kitties-fourth-litter',
  'shitty-cities-1',
  'shitty-city-blocks-1',
  'shitty-accessories',
];

const TheSecondaryPage = () => {
  const router = useRouter();
  const { query } = router;
  const [activeCollection, setActiveCollection] = useState('shitty-kitties');
  const [activeBlockRarity, setActiveBlockRarity] = useState('');
  const [activeLevel, setActiveLevel] = useState('');
  const [showMore, setShowMore] = useState(true);
  const [sortByPrice, setSortByPrice] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1); // State for current page
  const itemsPerPage = 25; // Define how many listings to show per page

  const collectionKey = useMemo(() => {
    return collectionKeys.find((key) => key === activeCollection);
  }, [activeCollection]);

  const { data: bountiesData } = useQuery({
    queryKey: ['bounties'],
    queryFn: () => fetch('/api/bounties/list').then((res) => res.json()),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchIntervalInBackground: false,
    staleTime: Infinity,
  });
  const bounties = bountiesData || [];

  const secondaryQuery = useQuery({
    enabled: !!collectionKey,
    queryKey: ['secondary', collectionKey],
    queryFn: async () => {
      const { data } = await axios.get(`/api/marketplace/listings?collection=${collectionKey}`);
      return data;
    },
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const secondary = secondaryQuery.data || [];

  const filteredSecondary = secondary
    .filter((listing) => {
      if (activeCollection === 'shitty-city-blocks-1' && activeBlockRarity) return listing.blockRarity === activeBlockRarity;
      return true;
    })
    .filter((listing) => {
      if (activeLevel) return listing.kittyLevel === parseInt(activeLevel);
      return true;
    })
    .sort((a, b) => {
      if (sortByPrice === 'asc') return a.price - b.price;
      return b.price - a.price;
    });

  const totalPages = Math.ceil(filteredSecondary.length / itemsPerPage); // Calculate total pages

  const handlePageChange = (newPage) => {
    // Function to handle page change
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const paginatedListings = filteredSecondary.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage); // Get listings for the current page

  const onSetActiveBlockRarity = (e) => {
    router.push(
      {
        pathname: '/marketplace',
        query: { ...query, rarity: e.target.value },
      },
      undefined,
      { scroll: false }
    );
  };

  const onSetActiveLevel = (e) => {
    router.push(
      {
        pathname: '/marketplace',
        query: { ...query, level: e.target.value },
      },
      undefined,
      { scroll: false }
    );
  };

  const onSortByPrice = () => {
    setSortByPrice(sortByPrice === 'asc' ? 'desc' : 'asc');
  };

  useEffect(() => {
    if (query.collection) {
      setActiveCollection(query.collection);
    } else {
      setActiveCollection('shitty-kitties');
    }
  }, [query.collection]);

  useEffect(() => {
    if (query.rarity) {
      setActiveBlockRarity(query.rarity);
    } else {
      setActiveBlockRarity('');
    }
  }, [query.rarity]);

  useEffect(() => {
    if (query.level) {
      setActiveLevel(query.level);
    } else {
      setActiveLevel('');
    }
  }, [query.level]);

  // if (secondaryQuery.isLoading)
  //   return (

  //   );

  return (
    <div className='-mt-6 md:mt-10 flex flex-col gap-6'>
      <div>
        <div className={clsx('flex items-center gap-3')}>
          <button type='button' className='font-medium text-neutral-400' onClick={() => setShowMore(!showMore)}>
            {showMore ? 'Hide Collections' : 'Show Collections'}
          </button>
        </div>

        {showMore && (
          <div className='grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-6 mt-4'>
            <CollectionButton
              image='/first-litter-collage.png'
              href={`/marketplace?collection=shitty-kitties`}
              heading='First Litter'
              active={activeCollection === 'shitty-kitties'}
            />
            <CollectionButton
              image='/second-litter-collage.png'
              href={`/marketplace?collection=shitty-kitties-slt`}
              heading='Second Litter'
              active={activeCollection === 'shitty-kitties-slt'}
            />
            <CollectionButton
              image='/third-litter-collage.png'
              href={`/marketplace?collection=shitty-kitties-third-litter`}
              heading='Third Litter'
              active={activeCollection === 'shitty-kitties-third-litter'}
            />
            <CollectionButton
              image='/twitter-share-image.png'
              href={`/marketplace?collection=shitty-kitties-fourth-litter`}
              heading='Fourth Litter'
              active={activeCollection === 'shitty-kitties-fourth-litter'}
            />
            <CollectionButton
              image='/shitty-city.png'
              href={`/marketplace?collection=shitty-cities-1`}
              heading='Shitty Cities'
              active={activeCollection === 'shitty-cities-1'}
            />
            <CollectionButton
              image='/city-blocks.png'
              href={`/marketplace?collection=shitty-city-blocks-1`}
              heading='Shitty City Blocks'
              active={activeCollection === 'shitty-city-blocks-1'}
            />
            <CollectionButton
              image='/accessories/shitty-litter-pan.png'
              href={`/marketplace?collection=shitty-accessories`}
              heading='Shitty Accessories'
              active={activeCollection === 'shitty-accessories'}
            />
          </div>
        )}
      </div>

      {secondaryQuery.isLoading ? (
        <div className='mt-16 flex justify-center'>
          <Spinner className='w-8 h-8' />
        </div>
      ) : (
        <>
          <div className='flex justify-between md:items-end flex-col md:flex-row gap-5'>
            <div>
              <p className='text-neutral-400'>
                Showing {formatNumber(paginatedListings.length)} of {formatNumber(filteredSecondary.length)} listings
              </p>
            </div>
            <div className='flex flex-col md:flex-row items-center justify-end gap-5'>
              <button
                type='button'
                className={`px-6 py-3 order-2 w-full md:w-auto md:order-1 border-2 border-transparent rounded-full lg:block transition-colors duration-150 ${
                  sortByPrice === 'desc'
                    ? 'bg-neutral-500 text-neutral-50 hover:bg-black'
                    : 'bg-neutral-200 text-neutral-900 hover:text-neutral-50 hover:bg-black'
                }`}
                onClick={onSortByPrice}
              >
                Sort by price: <strong>{sortByPrice === 'desc' ? 'High to low' : 'Low to high'}</strong>
              </button>

              {['First Litter', 'Second Litter', 'Third Litter', 'Fourth Litter'].includes(activeCollection) && (
                <select
                  value={activeLevel}
                  onChange={onSetActiveLevel}
                  placeholder='Block Rarity'
                  className='order-1 md:order-2 w-full md:w-auto form-input bg-gray-900 border-0 rounded-md py-3.5 focus:ring-2 focus:ring-white pr-10 text-lg'
                >
                  <option value='' disabled>
                    Filter by level
                  </option>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((choice) => (
                    <option key={choice} value={choice}>
                      {choice}
                    </option>
                  ))}
                </select>
              )}

              {activeCollection === 'Shitty City Blocks' && (
                <select
                  value={activeBlockRarity}
                  onChange={onSetActiveBlockRarity}
                  placeholder='Block Rarity'
                  className='order-1 md:order-2 w-full md:w-auto form-input bg-gray-900 border-0 rounded-md py-3.5 focus:ring-2 focus:ring-white pr-10 text-lg'
                >
                  <option value='' disabled>
                    Filter by rarity
                  </option>
                  {['Collab', 'Common', 'Uncommon', 'Rare', 'Ultra Rare', 'Legendary'].map((choice) => (
                    <option key={choice} value={choice}>
                      {choice}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5 mb-12'>
            {paginatedListings.map((listing, index) => (
              <SecondaryListing key={listing._id} listing={listing} isBounty={bounties.some((bounty) => bounty.assetId === listing.assetId)} />
            ))}
          </div>
        </>
      )}

      {totalPages > 1 && (
        <Pagination // Use the Pagination component
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      )}
    </div>
  );
};

export default TheSecondaryPage;
